import i18next from 'i18next';
import moment from 'moment-timezone';

import * as Warn from 'web-app/util/warnable';
import {InvoicingPeriod} from 'web-app/util/graphql/api-types';

import type * as Types from './types';

const DEFAULT_STATE: Types.State = {
    childrenState: {
        selectedChildren: [],
        allChildren: [],
        allSelectedChildrenHaveBillPayer: true,
    },
    tuitionsState: {
        inputFields: [{id: ''}],
        selectedTuitions: [],
        allSelectedTuitionsHaveTheSameFrequency: true,
        allTuitions: [],
        fieldCurrentlyOpen: null,
    },
    selectedBillingType: null,
    billingPeriodState: {
        selectedBillingPeriod: null,
        monthly: null,
        weekly: null,
        biweekly: null,
    },
    billingFrequencyState: {
        // Would have preferred a warning but EditBillingPlan reads this a
        // number of times because React before the state is initialized.
        initialBillingFrequency: Warn.resolve('monthly'),
        selectedBillingFrequency: null,
        billingFrequencyTypeToBeSetUp: null,
        billingFrequencies: [],
    },
    customPeriodState: {
        customPeriodDates: null,
    },
    feesState: {
        inputFields: [],
        selectedFees: [],
        allFees: [],
        fieldCurrentlyOpen: null,
    },
    discountState: {
        selectedDiscounts: [],
        allDiscounts: [],
    },
    endDateState: {selectedEndDate: null, error: null},
    showMoreOptions: false,
    pricingGroupState: {
        selectedPricingGroup: null,
        placement: 'none',
        hasAutomaticPricingGroup: false,
        monthlyPeriodPricingGroups: null,
        weeklyPeriodPricingGroups: null,
    },
    displayedModal: {type: 'none'},
    emptyPlan: {
        enabled: false,
        startDate: moment(),
        endDate: null,
    },
    template: {
        selectedTemplateId: null,
        shouldAddNewTemplate: false,
        newTemplateName: null,
    },
    isAutomaticBillingEnabled: false,
    isAutomaticDecouplingEnabled: false,
    childrenErrors: [],
    view: 'create',
    subView: 'bulk',
};

const INVOICE_PERIOD_OPTIONS: {
    label: string;
    value: InvoicingPeriod;
}[] = [
    {value: InvoicingPeriod.NEXT, label: i18next.t('automaticInvoicing.configuration.nextPeriod')},
    {value: InvoicingPeriod.CURRENT, label: i18next.t('automaticInvoicing.configuration.currentPeriod')},
    {value: InvoicingPeriod.PREVIOUS, label: i18next.t('automaticInvoicing.configuration.previousPeriod')},
];

/**
 * These static values are here only for the sake of the testing.
 * They will be adjusted according to feedback from US support team.
 */
const sharedCreationProps = {
    billOnChildSick: true,
    billOnChildAbsent: true,
    billOnClosingDays: ['0' as const, '1' as const, '2' as const],
};

const STATIC_PRODUCT_CREATION_PROPS = {
    ...sharedCreationProps,
    discountable: true,
    free: false,
    availability: [
        'MONDAY' as const,
        'TUESDAY' as const,
        'WEDNESDAY' as const,
        'THURSDAY' as const,
        'FRIDAY' as const,
        'SATURDAY' as const,
        'SUNDAY' as const,
    ],
    hiddenByUser: true,
};

const STATIC_PRODUCT_CREATION_PRICING_PROPS = {
    volume: 0,
};

const STATIC_SESSION_PACKAGE_CREATION_PROPS = {
    ...sharedCreationProps,
    flexibleHours: false,
    fundable: false,
    sessionType: 'package' as const,
    hiddenByUser: true,
};

const STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS = {
    price: 0,
    volume: 1,
};

const AUTOMATIC_PRICING_GROUP_ID = '01J46YBRFMHSCNXWATC0DRB9AX';

const BILLING_FREQUENCY_OPTIONS: Array<Types.BillingFrequencyOption> = [
    {
        type: 'monthly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.monthly'),
        value: 'monthly',
    },
    {
        type: 'biweekly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.biweekly'),
        value: 'biweekly',
    },
    {
        type: 'weekly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.weekly'),
        value: 'weekly',
    },
];

const AUTOMATIC_BILLING_OPTIONS: Array<Types.BillingFrequencyOption> = [
    {
        type: 'monthly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.monthly'),
        value: 'monthly',
        icon: {name: 'autopay'},
    },
    {
        type: 'biweekly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.biweekly'),
        value: 'biweekly',
        icon: {name: 'autopay'},
    },
    {
        type: 'weekly',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.weekly'),
        value: 'weekly',
        icon: {name: 'autopay'},
    },
    {
        type: 'none',
        label: i18next.t('react.simplifiedUsBillingPlan.autoBillingSetup.translations.json.doNotAutomate'),
        value: 'none',
    },
];

export {
    DEFAULT_STATE,
    INVOICE_PERIOD_OPTIONS,
    STATIC_PRODUCT_CREATION_PROPS,
    STATIC_PRODUCT_CREATION_PRICING_PROPS,
    STATIC_SESSION_PACKAGE_CREATION_PROPS,
    STATIC_SESSION_PACKAGE_CREATION_PRICING_PROPS,
    AUTOMATIC_PRICING_GROUP_ID,
    BILLING_FREQUENCY_OPTIONS,
    AUTOMATIC_BILLING_OPTIONS,
};
